import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type DemographicAnalysis } from '../../demographicsService'
import {
  PopulationTrendChart,
  type PopulationTrendChartRef,
} from '../charts/PopulationTrendChart'
import {
  GrowthComparisonChart,
  type GrowthComparisonChartRef,
} from '../charts/GrowthComparisonChart'
import {
  TabContentContainer,
  ChartGrid,
  ChartContainer,
} from '../../DemographicsTab.styles'

export interface GrowthAnalysisTabProps {
  data: DemographicAnalysis | null
  t: TFunction
}

export const GrowthAnalysisTab: React.FC<GrowthAnalysisTabProps> = ({
  data,
  t,
}) => {
  const populationTrendChartRef = useRef<PopulationTrendChartRef>(null)
  const growthComparisonChartRef = useRef<GrowthComparisonChartRef>(null)

  if (!data) return null

  const { current, trends } = data

  // Calculate annualized growth rates for comparison chart (CAGR)
  const calculateAnnualizedRate = (
    totalGrowthRate: number,
    years: number
  ): number => {
    return (Math.pow(1 + totalGrowthRate, 1 / years) - 1) * 100
  }

  const annualized1Year = calculateAnnualizedRate(
    current.total_population_evolution_1_year,
    1
  )
  const annualized2Year = calculateAnnualizedRate(
    current.total_population_evolution_2_year,
    2
  )
  const annualized5Year = calculateAnnualizedRate(
    current.total_population_evolution_5_year,
    5
  )
  const annualized10Year = calculateAnnualizedRate(
    current.total_population_evolution_10_year,
    10
  )

  return (
    <TabContentContainer>
      {/* Trend Charts */}
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.populationEvolution.title')}
            subtitle={t('charts.populationEvolution.subtitle')}
            onDownload={() => {
              const dataUrl = populationTrendChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.populationEvolution.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <PopulationTrendChart
            ref={populationTrendChartRef}
            data={trends}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.annualizedGrowthRate.title')}
            subtitle={t('charts.annualizedGrowthRate.subtitle')}
            onDownload={() => {
              const dataUrl = growthComparisonChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.annualizedGrowthRate.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <GrowthComparisonChart
            ref={growthComparisonChartRef}
            data={{
              [t('charts.growthPeriods.oneYear')]: annualized1Year,
              [t('charts.growthPeriods.twoYears')]: annualized2Year,
              [t('charts.growthPeriods.fiveYears')]: annualized5Year,
              [t('charts.growthPeriods.tenYears')]: annualized10Year,
            }}
            t={t}
          />
        </ChartContainer>
      </ChartGrid>
    </TabContentContainer>
  )
}
